import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IRootState } from 'src/infra/stores/store';
// Types moved from the deleted notificationStatusServiceSSE.ts
export interface NotificationStatus {
  unreadCount: number;
  lastUpdated: string;
}

export interface InvoiceProcessingStatusUpdate {
  invoiceFileProcessingId: string;
  fileName: string;
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
  progress?: number;
  error?: string;
  completedAt?: string;
  batchId?: string;
  serviceProviderCode?: string;
  fileResolutionStatus?: string;
  invoiceStatus?: string;
  // ✅ ADD MISSING FIELDS FROM SSE STREAM
  modalities?: ('ROAD' | 'SEA' | 'RAIL' | 'AIR' | 'COURIER' | 'CT')[]; // Transport modes
  invoiceId?: string; // Invoice ID when available
  shipmentCount?: number; // Number of shipments
  processingShipmentCount?: number; // Number of processing shipments
  // Add any other fields that come from SSE that you need
}

/**
 * SSE retry state interface
 */
export interface SSERetryState {
  attempts: number;
  maxAttempts: number;
  lastError: string | null;
  nextRetryAt: string | null; // ISO string for serialization
  isRetrying: boolean;
  canManualRetry: boolean;
}

/**
 * SSE Redux state interface
 */
export interface ISSEState {
  // Invoice processing updates from SSE
  invoiceUpdates: InvoiceProcessingStatusUpdate[];

  // Notification status from SSE
  notificationStatus: NotificationStatus;

  // Connection state
  isConnected: boolean;

  // Retry state for connection management
  retryState: SSERetryState;

  // Last activity timestamp for debugging
  lastActivity: string | null;
  // Latest download URL received from SSE
  latestDownloadUrl: string | null;

  // Downloading state
  isDownloading: boolean;
  downloadingFileType: string | null;

  // Background shipment result from SSE (recalculation toast)
  backgroundShipmentResult: {
    shipmentInvoiceId: string;
    invoiceId: string;
    result: any; // BackgroundShipmentPerformResultDto
  } | null;
}

const initialState: ISSEState = {
  invoiceUpdates: [],
  notificationStatus: {
    unreadCount: 0,
    lastUpdated: '',
  },
  isConnected: false,
  retryState: {
    attempts: 0,
    maxAttempts: 5,
    lastError: null,
    nextRetryAt: null,
    isRetrying: false,
    canManualRetry: false,
  },
  lastActivity: null,
  latestDownloadUrl: null,
  isDownloading: false,
  downloadingFileType: null,
  backgroundShipmentResult: null,
};

const sseSlice = createSlice({
  name: 'sse',
  initialState,
  reducers: {
    // Invoice processing updates
    invoiceUpdateReceived: (state, action: PayloadAction<InvoiceProcessingStatusUpdate>) => {
      state.invoiceUpdates.push(action.payload);
      state.lastActivity = new Date().toISOString();
    },

    // Clear old invoice updates (for new upload sessions)
    clearInvoiceUpdates: (state) => {
      state.invoiceUpdates = [];
      state.lastActivity = new Date().toISOString();
    },

    // Notification status updates
    notificationStatusReceived: (state, action: PayloadAction<NotificationStatus>) => {
      state.notificationStatus = action.payload;
      state.lastActivity = new Date().toISOString();
    },

    // Download started
    downloadStarted: (state, action: PayloadAction<string>) => {
      state.isDownloading = true;
      state.downloadingFileType = action.payload;
      state.lastActivity = new Date().toISOString();
    },

    // Download URL received
    downloadUrlReceived: (state, action: PayloadAction<string>) => {
      state.latestDownloadUrl = action.payload;
      state.isDownloading = false;
      // Keep downloadingFileType to show correct success message
      state.lastActivity = new Date().toISOString();
    },

    // Connection state management
    connectionChanged: (state, action: PayloadAction<boolean>) => {
      state.isConnected = action.payload;
      state.lastActivity = new Date().toISOString();

      // Reset retry state on successful connection
      if (action.payload) {
        state.retryState = {
          ...state.retryState,
          attempts: 0,
          lastError: null,
          nextRetryAt: null,
          isRetrying: false,
          canManualRetry: false,
        };
      }
    },

    // Retry state management
    retryAttemptStarted: (state, action: PayloadAction<{ attempt: number; nextRetryAt: string }>) => {
      state.retryState = {
        ...state.retryState,
        attempts: action.payload.attempt,
        nextRetryAt: action.payload.nextRetryAt,
        isRetrying: true,
        canManualRetry: false,
      };
      state.lastActivity = new Date().toISOString();
    },

    connectionFailed: (state, action: PayloadAction<{ reason: string; attempts: number }>) => {
      state.retryState = {
        ...state.retryState,
        attempts: action.payload.attempts,
        lastError: action.payload.reason,
        isRetrying: false,
        canManualRetry: true,
      };
      state.isConnected = false;
      state.isDownloading = false;
      state.downloadingFileType = null;
      state.lastActivity = new Date().toISOString();
    },

    // Manual retry reset
    retryStateReset: (state) => {
      state.retryState = {
        ...state.retryState,
        attempts: 0,
        lastError: null,
        canManualRetry: false,
        isRetrying: true,
      };
      state.lastActivity = new Date().toISOString();
    },

    // Manual retry requested (to be handled by SSEInitializer)
    manualRetryRequested: (state) => {
      state.retryState = {
        ...state.retryState,
        canManualRetry: false,
        isRetrying: true,
      };
      state.lastActivity = new Date().toISOString();
    },

    // Mark notifications as read (local state update)
    markNotificationAsRead: (state) => {
      if (state.notificationStatus.unreadCount > 0) {
        state.notificationStatus = {
          ...state.notificationStatus,
          unreadCount: state.notificationStatus.unreadCount - 1,
          lastUpdated: new Date().toISOString(),
        };
      }
      state.lastActivity = new Date().toISOString();
    },

    markAllNotificationsAsRead: (state) => {
      state.notificationStatus = {
        ...state.notificationStatus,
        unreadCount: 0,
        lastUpdated: new Date().toISOString(),
      };
      state.lastActivity = new Date().toISOString();
    },

    // Background shipment result from SSE (recalculation toast)
    backgroundShipmentResultReceived: (state, action: PayloadAction<{ shipmentInvoiceId: string; invoiceId: string; result: any }>) => {
      state.backgroundShipmentResult = action.payload;
      state.lastActivity = new Date().toISOString();
    },

    clearBackgroundShipmentResult: (state) => {
      state.backgroundShipmentResult = null;
    },
  },
});

// Export actions
export const {
  invoiceUpdateReceived,
  clearInvoiceUpdates,
  notificationStatusReceived,
  downloadStarted,
  downloadUrlReceived,
  connectionChanged,
  retryAttemptStarted,
  connectionFailed,
  retryStateReset,
  manualRetryRequested,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  backgroundShipmentResultReceived,
  clearBackgroundShipmentResult,
} = sseSlice.actions;

// Selectors
export const selectSSEState = (state: IRootState) => state.sse;
export const selectInvoiceUpdates = (state: IRootState) => state.sse.invoiceUpdates;
export const selectNotificationStatus = (state: IRootState) => state.sse.notificationStatus;
export const selectSSEConnection = (state: IRootState) => state.sse.isConnected;
export const selectSSERetryState = (state: IRootState) => state.sse.retryState;
export const selectSSELastActivity = (state: IRootState) => state.sse.lastActivity;
export const selectLatestDownloadUrl = (state: IRootState) => state.sse.latestDownloadUrl;
export const selectIsDownloading = (state: IRootState) => state.sse.isDownloading;
export const selectDownloadingFileType = (state: IRootState) => state.sse.downloadingFileType;

// Derived selectors
export const selectUnreadCount = (state: IRootState) => state.sse.notificationStatus.unreadCount;
export const selectCanManualRetry = (state: IRootState) => state.sse.retryState.canManualRetry;
export const selectBackgroundShipmentResult = (state: IRootState) => state.sse.backgroundShipmentResult;

export default sseSlice.reducer;
